import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

/**
 * Renders an "Install Admin App" prompt inside the admin dashboard.
 *
 * - Swaps the site's PWA manifest to /admin-manifest.json for the duration
 *   the admin app is open, so the browser's native "Install app" affordance
 *   installs THIS as its own icon (separate from the main outlawmedia.com.au
 *   PWA).
 * - Listens for the `beforeinstallprompt` event (Chrome/Edge/Android) and
 *   surfaces our own big red install button that fires it.
 * - Falls back to explicit "Add to Home Screen" instructions on iOS Safari
 *   which doesn't support beforeinstallprompt.
 * - Auto-hides once the app is installed.
 */
const AdminInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIos, setShowIos] = useState(false);
  const [showAndroidHelp, setShowAndroidHelp] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Swap manifest for admin-specific one on mount
  useEffect(() => {
    const existing = document.querySelector('link[rel="manifest"]');
    const prev = existing?.getAttribute('href') || '/manifest.json';
    if (existing) existing.setAttribute('href', '/admin-manifest.json');
    else {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/admin-manifest.json';
      document.head.appendChild(link);
    }
    // Also drop a matching theme colour meta so the status bar matches
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeMeta?.getAttribute('content');
    if (themeMeta) themeMeta.setAttribute('content', '#dc2626');

    return () => {
      if (existing) existing.setAttribute('href', prev);
      if (themeMeta && prevTheme) themeMeta.setAttribute('content', prevTheme);
    };
  }, []);

  useEffect(() => {
    const ua = navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/.test(ua);
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    if (isStandalone || localStorage.getItem('outlaw_admin_pwa_installed') === '1') {
      setInstalled(true);
      return () => {};
    }
    if (isIos) setShowIos(true);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      localStorage.setItem('outlaw_admin_pwa_installed', '1');
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // Fallback: if beforeinstallprompt never fires within 2.5s on Chrome/Android
    // (which happens when the main site's PWA scope already covers this URL, or
    // when Chrome has already installed something at this origin), surface
    // manual instructions so the admin can still install from the menu.
    const t = setTimeout(() => {
      if (!isIos && !isStandalone) setShowAndroidHelp(true);
    }, 2500);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      clearTimeout(t);
    };
  }, []);

  if (installed) return null;
  if (dismissed) return null;
  if (!deferred && !showIos && !showAndroidHelp) return null;

  const doInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') {
      setInstalled(true);
      localStorage.setItem('outlaw_admin_pwa_installed', '1');
    }
    setDeferred(null);
  };

  return (
    <div
      className="mb-4 rounded-xl border-2 border-red-500/50 bg-gradient-to-br from-red-950/40 via-black to-zinc-950 p-4 md:p-5 relative overflow-hidden"
      data-testid="admin-install-prompt"
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 text-gray-500 hover:text-white"
        aria-label="Dismiss install prompt"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3">
        <div className="shrink-0 h-11 w-11 rounded-lg bg-red-600 flex items-center justify-center">
          <Smartphone className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black tracking-[0.3em] uppercase text-red-400 mb-1">
            Install the Outlaw Admin App
          </p>
          <p className="text-sm text-gray-200 leading-relaxed">
            Add the admin to your home screen - publish articles, videos, moderate rumours and send
            push notifications straight from your phone. Works offline for content you&apos;ve already
            loaded.
          </p>

          {deferred && (
            <button
              type="button"
              onClick={doInstall}
              data-testid="admin-install-btn"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-widest transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Install App
            </button>
          )}

          {showIos && !deferred && (
            <div className="mt-3 text-xs text-gray-300 space-y-1 leading-relaxed">
              <p className="text-white font-bold text-[11px] tracking-widest uppercase">iPhone / iPad - how to install:</p>
              <p>1. Tap the <strong>Share</strong> icon at the bottom of Safari.</p>
              <p>2. Scroll down and choose <strong>Add to Home Screen</strong>.</p>
              <p>3. Confirm the name &ldquo;Outlaw Admin&rdquo; and tap <strong>Add</strong>.</p>
            </div>
          )}

          {showAndroidHelp && !deferred && !showIos && (
            <div className="mt-3 text-xs text-gray-300 space-y-2 leading-relaxed">
              <p className="text-white font-bold text-[11px] tracking-widest uppercase">Already have the main Outlaw Media app?</p>
              <p className="text-gray-400">
                Chrome won&apos;t install a second full PWA on the same domain. You have two options:
              </p>

              <div className="mt-2 border border-red-500/30 rounded-lg p-3 bg-red-950/20">
                <p className="text-red-400 font-bold text-[11px] tracking-widest uppercase mb-1">Option A - Recommended</p>
                <p className="text-gray-200">
                  Open your existing <strong>Outlaw Media</strong> app, then tap the URL bar (or use the browser
                  menu inside the app) and navigate to <strong>/q7-r3d-vault-9k2/dashboard</strong>. Bookmark it there.
                  It&apos;ll run inside the standalone app you already installed.
                </p>
              </div>

              <div className="mt-2 border border-gray-700 rounded-lg p-3 bg-zinc-900/40">
                <p className="text-gray-300 font-bold text-[11px] tracking-widest uppercase mb-1">Option B - Chrome shortcut</p>
                <p className="text-gray-400">
                  Tap Chrome&apos;s <strong>⋮ menu → Add to Home screen → Create shortcut</strong>. This adds an
                  Admin icon that opens in a Chrome tab (not fully standalone, but always one tap away).
                </p>
              </div>

              <div className="mt-2 border border-gray-700 rounded-lg p-3 bg-zinc-900/40">
                <p className="text-gray-300 font-bold text-[11px] tracking-widest uppercase mb-1">Option C - Full separate app</p>
                <p className="text-gray-400">
                  Uninstall the main Outlaw Media PWA first, then reload this admin page and tap <strong>Install</strong>.
                  You can reinstall the main app afterwards.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminInstallPrompt;
